import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Stepper,
  Step,
  StepButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import {
  CloudUpload as UploadIcon,
  PeopleAlt as PeopleIcon,
} from "@mui/icons-material";
import UploadStep from "./UploadStep";
import PreviewStep from "./PreviewStep";
import OptionsStep from "./OptionsStep";
import SuccessStep from "./SuccessStep";
import { MOCK_USERS } from "../../test-data";

const ImportCustomersDialog = ({ allUsers = [], onApply }) => {
  const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10MB
  const ALLOWED_EXTENSIONS = ["csv"];
  const PANEL_MIN_HEIGHT = 295;
  const DIALOG_HEIGHT = 620;
  const steps = ["Upload", "Preview", "Options"];

  const [open, setOpen] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [deleteExisting, setDeleteExisting] = useState(false);
  const [file, setFile] = useState(null);
  const [step, setStep] = useState("upload"); // 'upload' | 'preview' | 'options' | 'success'
  const [parsedIds, setParsedIds] = useState([]);
  const [errorMsg, setErrorMsg] = useState(null);
  const fileInputRef = useRef(null);
  const [saving, setSaving] = useState(false);

  const userOptions = useMemo(
    () =>
      Array.isArray(allUsers) && allUsers.length > 0 ? allUsers : MOCK_USERS,
    [allUsers]
  );

  useEffect(() => {
    if (!open) return;
    setSelectedUsers([]);
    setDeleteExisting(false);
    setFile(null);
    setStep("upload");
    setParsedIds([]);
    setErrorMsg(null);
  }, [open]);

  const readFileAsText = (blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result || "");
      reader.onerror = reject;
      reader.readAsText(blob);
    });

  const parseCustomerIdsFromText = (text) => {
    const tokens = String(text)
      .split(/[\r\n,;\t\s]+/)
      .map((t) => t.trim());
    const uniqueIds = [];
    const seen = new Set();
    for (const tok of tokens) {
      if (!tok) continue;
      if (/^\d+$/.test(tok)) {
        if (!seen.has(tok)) {
          seen.add(tok);
          uniqueIds.push(tok);
        }
      }
    }
    return uniqueIds;
  };

  const handleParsedFile = (f, text) => {
    const ids = parseCustomerIdsFromText(text);
    setParsedIds(ids);
    const noIds = ids.length === 0;
    setErrorMsg(noIds ? "No valid customer IDs found in the file." : null);
    setStep(noIds ? "upload" : "preview");
  };

  const isAllowedFile = (f) => {
    const ext = (f.name.split(".").pop() || "").toLowerCase();

    return ALLOWED_EXTENSIONS.includes(ext);
  };

  const handleFileSelected = async (f) => {
    if (!f) return;
    // reset input so the same file can be selected again
    if (fileInputRef.current) fileInputRef.current.value = "";

    setErrorMsg(null);
    if (!isAllowedFile(f)) {
      setParsedIds([]);
      setErrorMsg("Invalid file type. Please upload a .csv file.");
      setStep("upload");
      return;
    }

    if (f.size > MAX_FILE_SIZE_BYTES) {
      setParsedIds([]);
      setErrorMsg("File is too large. Max size is 10MB.");
      setStep("upload");
      return;
    }

    setFile(f);
    try {
      const text = await readFileAsText(f);
      handleParsedFile(f, text);
    } catch (_err) {
      setParsedIds([]);
      setErrorMsg("Unable to read the file. Please try again.");
      setStep("upload");
    }
  };

  const handleFileChange = async (e) => {
    const f = e?.target?.files?.[0] || null;
    await handleFileSelected(f);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    const f = e.dataTransfer?.files?.[0] || null;
    await handleFileSelected(f);
  };

  const preventDefault = (e) => e.preventDefault();

  const handleApply = () => {
    if (!file || selectedUsers.length === 0 || parsedIds.length === 0) return;
    const payload = {
      file,
      customerIds: parsedIds,
      deleteExisting,
      targetUserIds: selectedUsers.map((u) => u.id),
    };

    if (onApply) onApply(payload);
  };

  const fileLabel = file
    ? file.name
    : "Drop your CSV file here or click to browse";
  const stepOrder = ["upload", "preview", "options"];
  const activeStep = Math.max(0, stepOrder.indexOf(step));

  return (
    <>
      <Button
        variant="contained"
        onClick={() => setOpen(true)}
        startIcon={<UploadIcon />}
        size="medium"
        sx={{
          fontSize: "1.2rem",
          backgroundColor: "#757575",
          color: "white",
          width: { xs: "100%", lg: "auto" },
          "&:hover": { backgroundColor: "#616161" },
        }}
      >
        Import Customers
      </Button>

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
        slotProps={{
          paper: { sx: { height: DIALOG_HEIGHT, display: "flex" } },
        }}
      >
        <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              mb: 2,
            }}
          >
            <Box
              sx={{
                width: { xs: 36, sm: 44 },
                height: { xs: 36, sm: 44 },
                borderRadius: 2,
                bgcolor: "#2f3b4a",
                boxShadow: "0 6px 16px rgba(0,0,0,0.25)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <PeopleIcon
                sx={{ fontSize: { xs: 18, sm: 22 }, color: "#fff" }}
              />
            </Box>
          </Box>

          <Typography
            variant="h5"
            sx={{ fontWeight: 600, fontSize: { xs: "1.8rem", sm: "2rem" } }}
          >
            Import Customers
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: "text.secondary",
              mt: 0.5,
              fontSize: { xs: "1.2rem", sm: "1.3rem" },
            }}
          >
            Import customer data from CSV files to your system
          </Typography>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            background:
              "linear-gradient(180deg, #ffffff 0%, #fafafa 60%, #ffffff 100%)",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {step !== "success" && (
            <Stepper
              activeStep={activeStep}
              alternativeLabel
              sx={{
                mb: 2,
                flexShrink: 0,
                "& .MuiStepIcon-root": {
                  fontSize: { xs: "1.8rem", sm: "2rem" },
                },
                "& .MuiStepIcon-text": {
                  fontSize: { xs: "1rem", sm: "1.1rem" },
                  fontWeight: 700,
                },
              }}
            >
              {steps.map((label, idx) => (
                <Step key={label}>
                  <StepButton
                    onClick={() => {
                      if (idx <= activeStep) {
                        setStep(stepOrder[idx]);
                      }
                    }}
                    sx={{
                      "& .MuiStepLabel-label": {
                        fontSize: { xs: "1.1rem", sm: "1.2rem" },
                        fontWeight: 600,
                      },
                      "&:hover .MuiStepLabel-label": {
                        textDecoration: "underline",
                      },
                    }}
                  >
                    {label}
                  </StepButton>
                </Step>
              ))}
            </Stepper>
          )}

          {/* Step Panels */}
          {step === "upload" ? (
            <UploadStep
              fileLabel={fileLabel}
              fileInputRef={fileInputRef}
              onFileChange={handleFileChange}
              onDrop={handleDrop}
              preventDefault={preventDefault}
              errorMsg={errorMsg}
            />
          ) : step === "preview" ? (
            <PreviewStep
              parsedIds={parsedIds}
              errorMsg={errorMsg}
              minHeight={PANEL_MIN_HEIGHT}
            />
          ) : step === "options" ? (
            <OptionsStep
              userOptions={userOptions}
              selectedUsers={selectedUsers}
              setSelectedUsers={setSelectedUsers}
              deleteExisting={deleteExisting}
              setDeleteExisting={setDeleteExisting}
            />
          ) : (
            <SuccessStep
              parsedIds={parsedIds}
              selectedUsers={selectedUsers}
              deleteExisting={deleteExisting}
              minHeight={PANEL_MIN_HEIGHT}
            />
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          {step !== "success" && (
            <Button
              variant="outlined"
              onClick={() => setOpen(false)}
              color="inherit"
              size="medium"
              sx={{
                fontSize: { xs: "1.2rem", sm: "1.3rem" },
                width: { xs: "100%", sm: "auto" },
              }}
            >
              Cancel
            </Button>
          )}

          {step === "upload" && (
            <Button
              variant="contained"
              disabled
              size="medium"
              sx={{
                fontSize: { xs: "1.2rem", sm: "1.3rem" },
                width: { xs: "100%", sm: "auto" },
              }}
            >
              Next
            </Button>
          )}

          {step === "preview" && (
            <Button
              variant="contained"
              onClick={() => setStep("options")}
              disabled={!(parsedIds.length > 0 && !errorMsg)}
              size="medium"
              sx={{
                fontSize: { xs: "1.2rem", sm: "1.3rem" },
                width: { xs: "100%", sm: "auto" },
              }}
            >
              Next
            </Button>
          )}

          {step === "options" && (
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={async () => {
                  setSaving(true);
                  await new Promise((r) => setTimeout(r, 800));
                  handleApply();
                  setSaving(false);
                  setStep("success");
                }}
                disabled={
                  selectedUsers.length === 0 || parsedIds.length === 0 || saving
                }
                size="medium"
                sx={{
                  fontSize: { xs: "1.2rem", sm: "1.3rem" },
                  width: { xs: "100%", sm: "auto" },
                }}
                startIcon={<UploadIcon />}
              >
                {saving ? "Importing..." : "Import"}
              </Button>
            </Box>
          )}

          {step === "success" && (
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                onClick={() => {
                  setOpen(false);
                }}
                size="medium"
                sx={{
                  fontSize: { xs: "1.2rem", sm: "1.3rem" },
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                Close
              </Button>
            </Box>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ImportCustomersDialog;
